import { apiSlice } from "../../../features/api/apiSlice";

export type NecOffice = {
  id: number;
  name: string;
  slug?: string;
  description?: string | null;
  rank?: number | null;
  role_id?: number | null;
  role?: {
    id: number;
    name: string;
    guard_name?: string;
    permissions?: any[];
  } | null;
  created_at?: string;
  updated_at?: string;
  necs?: any;
};

type ApiListResponse<T> = {
  status: "success" | "error";
  data: T;
  message?: string;
};

type ApiItemResponse<T> = {
  status: "success" | "error";
  data: T;
  message?: string;
};

type ApiMessageResponse = {
  status: "success" | "error";
  message?: string;
};

export const necOfficeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNecOffices: builder.query<NecOffice[], void>({
      query: () => ({
        url: "/nec-offices",
        method: "GET",
      }),
      transformResponse: (res: ApiListResponse<NecOffice[]>) => res?.data ?? [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((o) => ({ type: "NecOffice" as const, id: o.id })),
              { type: "NecOffice" as const, id: "LIST" },
            ]
          : [{ type: "NecOffice" as const, id: "LIST" }],
    }),

    getNecOfficeDetails: builder.query<any, number>({
      query: (id) => ({ url: `/nec-offices/${id}`, method: "GET" }),
      transformResponse: (res: any) => res?.data,
      providesTags: (_res, _err, id) => [{ type: "NecOffice", id }],
    }),

    getNecOffice: builder.query<NecOffice, number | string>({
      query: (id) => ({
        url: `/nec-offices/${id}`,
        method: "GET",
      }),
      transformResponse: (res: ApiItemResponse<NecOffice>) => res?.data,
      providesTags: (_res, _err, id) => [{ type: "NecOffice", id: Number(id) }],
    }),

    createNecOffice: builder.mutation<
      ApiItemResponse<NecOffice>,
      { name: string; description?: string; rank?: number }
    >({
      query: (body) => ({
        url: "/nec-offices",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NecOffice", id: "LIST" }],
    }),

    updateNecOffice: builder.mutation<
      ApiItemResponse<NecOffice>,
      {
        id: number | string;
        name?: string;
        description?: string | null;
        rank?: number | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/nec-offices/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "NecOffice", id: Number(arg.id) },
        { type: "NecOffice", id: "LIST" },
      ],
    }),

    deleteNecOffice: builder.mutation<ApiMessageResponse, number | string>({
      query: (id) => ({
        url: `/nec-offices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "NecOffice", id: Number(id) },
        { type: "NecOffice", id: "LIST" },
      ],
    }),

    // ✅ NEC assignment uses SAME executive_set_id
    assignMemberToNecOffice: builder.mutation<any, any>({
      query: (body) => ({
        url: "/necs/assign",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NecOffice", id: "LIST" }],
    }),

    // Remove NEC assignment by necId
    removeNec: builder.mutation<any, number>({
      query: (necId) => ({
        url: `/necs/${necId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "NecOffice", id: "LIST" }],
    }),

    // Move NEC assignment by necId
    moveNec: builder.mutation<any, { necId: number; nec_office_id: number }>({
      query: ({ necId, nec_office_id }) => ({
        url: `/necs/${necId}/move`,
        method: "PUT",
        body: { office_id: nec_office_id },
      }),
      invalidatesTags: [{ type: "NecOffice", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNecOfficesQuery,
  useGetNecOfficeQuery,
  useGetNecOfficeDetailsQuery,
  useCreateNecOfficeMutation,
  useUpdateNecOfficeMutation,
  useDeleteNecOfficeMutation,
  useAssignMemberToNecOfficeMutation,

  useRemoveNecMutation,
  useMoveNecMutation,
} = necOfficeApi;
